import { useState, useCallback, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Button } from '@/components/ui/button';
import { type NoteImage as NoteImageType } from '@/api/client-notes';
import { useNoteImage } from '../hooks/useNoteImage';

interface ViewerImageProps {
  clientId: number;
  noteId: string;
  imageId: string;
  alt: string;
}

function ViewerImage({ clientId, noteId, imageId, alt }: ViewerImageProps) {
  const { data: src, isLoading } = useNoteImage({
    clientId,
    noteId,
    imageId,
    thumb: false,
  });

  if (isLoading || !src) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="max-w-full max-h-full object-contain"
      draggable={false}
    />
  );
}

interface ImageViewerProps {
  clientId: number;
  noteId: string;
  images: NoteImageType[];
  initialIndex: number;
  open: boolean;
  onClose: () => void;
}

export function ImageViewer({
  clientId,
  noteId,
  images,
  initialIndex,
  open,
  onClose,
}: ImageViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset to initialIndex when viewer opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    },
    [onClose, goToPrevious, goToNext],
  );

  if (!open || images.length === 0) {
    return null;
  }

  const currentImage = images[currentIndex];

  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white z-10">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-white/20"
        >
          <X className="h-6 w-6" />
        </Button>
        {images.length > 1 && (
          <span className="text-sm">
            {currentIndex + 1} / {images.length}
          </span>
        )}
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Image with zoom */}
      <div className="flex-1 relative">
        <TransformWrapper
          key={currentIndex} // Reset zoom when changing images
          initialScale={1}
          minScale={0.5}
          maxScale={4}
          centerOnInit
          wheel={{ step: 0.1 }}
          pinch={{ step: 5 }}
          doubleClick={{ mode: 'toggle' }}
        >
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
            }}
            contentStyle={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ViewerImage
              clientId={clientId}
              noteId={noteId}
              imageId={currentImage.id}
              alt={currentImage.originalName}
            />
          </TransformComponent>
        </TransformWrapper>

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12"
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12"
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
